import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import {
  BookOpen,
  ShieldCheck,
  Sparkles,
  Store,
  Newspaper,
  CalendarDays,
  Lock,
  Fingerprint,
  KeyRound,
  Landmark,
  BadgeCheck,
  FileText,
  Wallet,
  Ticket,
  ArrowRight,
  X,
} from "lucide-react";
import { Button } from "../components/ui/button";
import { Dialog, DialogContent, DialogTitle } from "../components/ui/dialog";
import { SiteNavigation } from "../components/navigation/SiteNavigation";

/* ------------------------------------------------------------------
   Content — walkthroughs, security topics, teaching videos
   Everything lives inline as data so we can localise / reorder later.
------------------------------------------------------------------ */

const PRODUCT_WALKTHROUGHS = [
  {
    id: "store",
    name: "Store",
    icon: Store,
    tagline: "Sell music, movies, merch and experiences.",
    accent: "#32D583",
    steps: [
      {
        title: "1 · Add your first product",
        body: "Head to Dashboard → Store → New product. Upload your full music or video file, at least 60 seconds long. StardomFans generates a shareable 60-second preview with our brand watermark.",
      },
      {
        title: "2 · Price in Tokens",
        body: "1 Token = $1 USD. Set a fair price — fans top up their wallet fee-free (no tax at top-up). Sales tax is added at product checkout and shown to fans before they confirm.",
      },
      {
        title: "3 · Ship or stream",
        body: "Digital products download instantly with a per-buyer watermark. Physical products push a shipping label task to your dashboard the moment a fan checks out.",
      },
      {
        title: "4 · Get paid on your cadence",
        body: "Every matured sale flows into your Token wallet. On your chosen cadence (Day-11 / biweekly / monthly), a real Stripe Connect transfer fires for your split (88% Standard, 90% Founders Circle) minus Stripe processing + payout fees.",
      },
    ],
  },
  {
    id: "feed",
    name: "Feed & Headlines",
    icon: Newspaper,
    tagline: "Post entertainment news, updates and long-form thoughts.",
    accent: "#A78BFA",
    steps: [
      {
        title: "1 · Compose without limits",
        body: "Headlines have no character cap — write a two-line status, a full essay, a manifesto. Attach an image or a 60s watermarked clip.",
      },
      {
        title: "2 · Public or private",
        body: "Every post is public by default (community feed). Toggle visibility to 'personal' for close-friends-style updates.",
      },
      {
        title: "3 · Engage, don't algorithm",
        body: "Fans can Like, Reply, Repost, Bookmark and Copy your post. There's no shadow-ban engine — every follower sees every post in reverse-chron.",
      },
      {
        title: "4 · Copy → share anywhere",
        body: "Tap the Copy icon on any post to paste it into Instagram, TikTok, Twitter — the shareable 60s hook + your text travel with it.",
      },
    ],
  },
  {
    id: "events",
    name: "Events & Tickets",
    icon: CalendarDays,
    tagline: "Sell cryptographic tickets to shows, meet-and-greets, drops.",
    accent: "#8DB87A",
    steps: [
      {
        title: "1 · Create the event",
        body: "Dashboard → Events → New event. Add title, date, venue (physical or livestream URL), and ticket tiers with limited-count support.",
      },
      {
        title: "2 · Fans pay in Tokens",
        body: "Each ticket is issued as a cryptographic PDF + QR code, signed by StardomFans Ltd. Scanner rejects any duplicate scan in real-time.",
      },
      {
        title: "3 · Scan at the door",
        body: "Use the built-in Ticket Scanner on your phone — camera scans the QR, backend validates the signature + burn state in one call. No third-party ticketing app required.",
      },
      {
        title: "4 · Post-event Token wire",
        body: "Ticket sales settle into your Wallet like any other Token credit and follow the same Day-11 payout schedule.",
      },
    ],
  },
];

const SECURITY_TOPICS = [
  {
    id: "identity",
    icon: BadgeCheck,
    title: "Secure Digital Identity",
    body:
      "Every account on StardomFans — fan, artist, admin — is verified against a government-issued document before any money moves. Our verification process runs on encrypted infrastructure and is designed so that no single employee can bypass it.",
    stat: "Verified accounts only · Zero anonymous transactions",
  },
  {
    id: "content_shield",
    icon: Fingerprint,
    title: "Proprietary IP Protection",
    body:
      "Every music, video and film download released through StardomFans carries proprietary buyer-anchored protection. If a file ever surfaces where it shouldn't, our IP-recovery team can trace it back to the origin — and produce prosecution-ready evidence.",
    stat: "Every download uniquely protected",
  },
  {
    id: "session_shield",
    icon: KeyRound,
    title: "Live Session Guard",
    body:
      "See every device signed in to your account with location details. Foreign or unfamiliar sessions are flagged instantly, and you can revoke any device — or every device — with one tap. Sensitive admin actions require a second-factor unlock, valid for a short window.",
    stat: "Instant device revocation · One-tap kill switch",
  },
  {
    id: "vault",
    icon: Landmark,
    title: "Segregated Treasury Vault",
    body:
      "Sales tax collected at checkout is held in a segregated treasury account, isolated from the operating balance. Funds can only be remitted through a signed, dual-controlled release process — even a compromised admin account cannot touch it.",
    stat: "Tax funds ring-fenced · Dual-control release",
  },
  {
    id: "fraud_shield",
    icon: ShieldCheck,
    title: "Real-time Fraud Shield",
    body:
      "Our security layer continuously monitors sign-in, checkout, upload and payout activity for anomalies. Suspicious patterns are throttled at the perimeter before they ever reach your feed, wallet or storefront.",
    stat: "24/7 automated monitoring",
  },
  {
    id: "payout_shield",
    icon: Wallet,
    title: "Identity-anchored Payouts",
    body:
      "Every payout — automated or manual — is locked to the verified legal identity on file. Bank details cannot be silently changed, and payouts refuse to run if the beneficiary name doesn't match. This protects artists from account-takeover fraud.",
    stat: "Beneficiary name lock · Fraud-resistant payouts",
  },
  {
    id: "compliance",
    icon: FileText,
    title: "Multi-Layer Bank-Grade Security",
    body:
      "StardomFans is built end-to-end with defense-in-depth. Every layer — identity, payments, uploads, downloads, payouts and admin access — is independently protected and independently audited. If one layer is ever tested, the others still hold.",
    stat: "Independent layers · Independent audits",
  },
];

/* ------------------------------------------------------------------ */

const WalkthroughModal = ({ product, onClose }) => {
  const [step, setStep] = useState(0);
  useEffect(() => setStep(0), [product]);
  if (!product) return null;

  const Icon = product.icon;
  const s = product.steps[step];
  const isLast = step === product.steps.length - 1;

  return (
    <Dialog open={!!product} onOpenChange={(open) => !open && onClose()}>
      <DialogContent
        className="max-w-2xl bg-[#0A0E18] border-[#1A2030] text-white p-0 overflow-hidden [&>button]:hidden"
        data-testid={`walkthrough-modal-${product.id}`}
      >
        <DialogTitle className="sr-only">{product.name} walkthrough</DialogTitle>
        <div className="relative overflow-hidden">
          <div
            aria-hidden="true"
            className="absolute top-0 left-0 right-0 h-[3px] sf-silver-plate sf-silver-shine-animate"
          />
          <button
            onClick={onClose}
            className="absolute top-4 right-4 text-[#8A92A0] hover:text-white z-10"
            data-testid="walkthrough-close"
            aria-label="Close walkthrough"
          >
            <X className="w-5 h-5" />
          </button>

          <div className="px-8 pt-10 pb-6">
            <div className="flex items-center gap-3 mb-2">
              <div
                className="w-10 h-10 rounded-full flex items-center justify-center"
                style={{ background: `${product.accent}22`, color: product.accent }}
              >
                <Icon className="w-5 h-5" />
              </div>
              <div>
                <p className="text-[10px] uppercase tracking-widest text-[#8A92A0]">
                  Walkthrough · {product.name}
                </p>
                <h2 className="font-['Unbounded'] text-xl font-bold text-white">{product.tagline}</h2>
              </div>
            </div>

            {/* Step indicator */}
            <div className="flex gap-1 mt-6 mb-6">
              {product.steps.map((_, i) => (
                <button
                  key={i}
                  onClick={() => setStep(i)}
                  className={`h-1.5 flex-1 rounded-full transition-all ${
                    i === step ? "bg-[#EC008C]" : i < step ? "bg-[#EC008C]/40" : "bg-[#1A2030]"
                  }`}
                  data-testid={`walkthrough-dot-${product.id}-${i}`}
                  aria-label={`Go to step ${i + 1}`}
                />
              ))}
            </div>

            {/* Step content */}
            <div className="min-h-[140px]" data-testid={`walkthrough-step-${step}`}>
              <h3 className="font-['Unbounded'] text-lg font-bold text-white mb-2">{s.title}</h3>
              <p className="text-[#c0c9d4] leading-relaxed">{s.body}</p>
            </div>

            <div className="flex items-center justify-between mt-8 pt-4 border-t border-[#1A2030]">
              <Button
                variant="ghost"
                onClick={() => setStep(Math.max(0, step - 1))}
                disabled={step === 0}
                className="text-[#8A92A0] hover:text-white disabled:opacity-30"
                data-testid="walkthrough-back"
              >
                ← Back
              </Button>
              <span className="text-xs text-[#8A92A0]">
                {step + 1} of {product.steps.length}
              </span>
              {isLast ? (
                <Button
                  onClick={onClose}
                  className="bg-[#EC008C] text-black font-bold hover:bg-[#FF3399]"
                  data-testid="walkthrough-finish"
                >
                  Got it — let&apos;s go
                </Button>
              ) : (
                <Button
                  onClick={() => setStep(step + 1)}
                  className="bg-[#EC008C] text-black font-bold hover:bg-[#FF3399]"
                  data-testid="walkthrough-next"
                >
                  Next <ArrowRight className="w-4 h-4 ml-1.5" />
                </Button>
              )}
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

/* ------------------------------------------------------------------ */

const TeachingCenter = () => {
  const [tab, setTab] = useState("walkthroughs");
  const [activeProduct, setActiveProduct] = useState(null);

  const activeTabClass = "text-white border-[#EC008C]";
  const idleTabClass = "text-[#8A92A0] border-transparent hover:text-white";
  const tabs = useMemo(
    () => [
      { id: "walkthroughs", label: "Product Walkthroughs", icon: Sparkles },
      { id: "security", label: "Security & Transparency", icon: ShieldCheck },
    ],
    []
  );

  return (
    <div className="min-h-screen bg-[#050810] text-white" data-testid="teaching-center-page">
      <header className="border-b border-[#1A2030] public-navigation-header">
        <div className="max-w-6xl mx-auto px-6 py-4 flex items-center justify-between gap-4">
          <Link to="/home" data-testid="learn-home-link"><img src="/logo.png" alt="StardomFans" className="object-contain" /></Link>
          <SiteNavigation scope="learn" />
        </div>
      </header>
      {/* Hero */}
      <section className="relative overflow-hidden border-b border-[#1A2030]">
        <div
          aria-hidden="true"
          className="absolute inset-x-0 top-0 h-[3px] sf-silver-plate sf-silver-shine-animate"
        />
        <div className="max-w-6xl mx-auto px-6 py-16">
          <div className="flex items-center gap-3 mb-3">
            <span className="sf-silver-chip">
              <BookOpen className="w-3 h-3" />
              Teaching Center
            </span>
          </div>
          <h1 className="font-['Unbounded'] text-4xl sm:text-5xl lg:text-6xl font-bold text-white leading-tight">
            Learn StardomFans in
            <br />
            <span className="text-[#EC008C]">under ten minutes.</span>
          </h1>
          <p className="text-[#8A92A0] text-lg mt-4 max-w-2xl">
            Every product, every safeguard, every reason we can call ourselves a
            bank-grade creator platform — laid out so anyone (fan, artist, journalist,
            regulator) can follow along.
          </p>
        </div>
      </section>

      {/* Tabs */}
      <div className="sticky top-0 z-30 bg-[#050810]/95 backdrop-blur border-b border-[#1A2030]">
        <div className="max-w-6xl mx-auto px-6 flex flex-wrap gap-1">
          {tabs.map((t) => {
            const Icon = t.icon;
            return (
              <button
                key={t.id}
                onClick={() => setTab(t.id)}
                className={`inline-flex items-center gap-2 px-3 py-4 border-b-2 text-sm font-semibold transition-colors ${
                  tab === t.id ? activeTabClass : idleTabClass
                }`}
                data-testid={`tab-${t.id}`}
              >
                <Icon className="w-4 h-4" />
                {t.label}
              </button>
            );
          })}
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-6 py-12">
        {/* --- Walkthroughs --- */}
        {tab === "walkthroughs" && (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4" data-testid="walkthroughs-grid">
            {PRODUCT_WALKTHROUGHS.map((p) => {
              const Icon = p.icon;
              return (
                <button
                  key={p.id}
                  onClick={() => setActiveProduct(p)}
                  className="text-left rounded-2xl border border-[#1A2030] bg-[#0A0E18] p-6 hover:border-[#EC008C]/50 transition-all group"
                  data-testid={`walkthrough-card-${p.id}`}
                >
                  <div
                    className="w-12 h-12 rounded-xl flex items-center justify-center mb-4"
                    style={{ background: `${p.accent}22`, color: p.accent }}
                  >
                    <Icon className="w-6 h-6" />
                  </div>
                  <h3 className="font-['Unbounded'] text-xl font-bold text-white mb-1">{p.name}</h3>
                  <p className="text-sm text-[#8A92A0] mb-4">{p.tagline}</p>
                  <span className="inline-flex items-center gap-1 text-xs text-[#EC008C] font-semibold group-hover:gap-2 transition-all">
                    Start walkthrough <ArrowRight className="w-3 h-3" />
                  </span>
                </button>
              );
            })}
          </div>
        )}

        {/* --- Security & Transparency --- */}
        {tab === "security" && (
          <div className="space-y-8" data-testid="security-tab">
            <div className="rounded-2xl border border-[#EC008C]/30 bg-gradient-to-br from-[#EC008C]/[0.06] to-transparent p-6">
              <div className="flex items-center gap-2 mb-2">
                <span className="sf-silver-chip">
                  <ShieldCheck className="w-3 h-3" />
                  Bank-Grade
                </span>
              </div>
              <h2 className="font-['Unbounded'] text-2xl font-bold text-white mb-2">
                Every safeguard, in plain English.
              </h2>
              <p className="text-[#c0c9d4] max-w-3xl">
                StardomFans is engineered to keep artists paid, fans safe, and pirates
                out. Here&apos;s every layer that runs on the platform — no fine print,
                no marketing waffle.
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-testid="security-grid">
              {SECURITY_TOPICS.map((t) => {
                const Icon = t.icon;
                return (
                  <div
                    key={t.id}
                    className="rounded-2xl border border-[#1A2030] bg-[#0A0E18] p-6"
                    data-testid={`security-card-${t.id}`}
                  >
                    <div className="flex items-center gap-3 mb-3">
                      <div className="w-10 h-10 rounded-xl bg-[#EC008C]/15 text-[#EC008C] flex items-center justify-center">
                        <Icon className="w-5 h-5" />
                      </div>
                      <h3 className="font-['Unbounded'] text-lg font-bold text-white">{t.title}</h3>
                    </div>
                    <p className="text-sm text-[#c0c9d4] leading-relaxed mb-4">{t.body}</p>
                    <div className="sf-silver-chip">
                      <Lock className="w-3 h-3" />
                      {t.stat}
                    </div>
                  </div>
                );
              })}
            </div>

            <div className="rounded-2xl border border-[#1A2030] bg-[#0A0E18] p-6 flex items-center justify-between gap-4 flex-wrap">
              <div>
                <h3 className="font-['Unbounded'] text-lg font-bold text-white">
                  Read the full trust documents.
                </h3>
                <p className="text-sm text-[#8A92A0]">
                  Our public Security Trust page and Privacy Policy — the plain-English
                  version of how we protect artists, fans and every dollar on the
                  platform. For anything else, write to info@stardomfans.com.
                </p>
              </div>
              <div className="flex gap-2">
                <Link
                  to="/security"
                  className="inline-flex items-center gap-2 h-10 px-4 rounded-full border border-[#1A2030] text-white hover:border-[#EC008C]/50 text-sm"
                  data-testid="link-security-trust"
                >
                  <ShieldCheck className="w-4 h-4" /> Security Trust
                </Link>
                <Link
                  to="/privacy"
                  className="inline-flex items-center gap-2 h-10 px-4 rounded-full border border-[#1A2030] text-white hover:border-[#EC008C]/50 text-sm"
                  data-testid="link-privacy-policy"
                >
                  <FileText className="w-4 h-4" /> Privacy Policy
                </Link>
              </div>
            </div>
          </div>
        )}
      </div>

      <WalkthroughModal product={activeProduct} onClose={() => setActiveProduct(null)} />

      {/* Footer CTA */}
      <section className="border-t border-[#1A2030] bg-black">
        <div className="max-w-6xl mx-auto px-6 py-12 flex items-center justify-between gap-6 flex-wrap">
          <div>
            <h3 className="font-['Unbounded'] text-2xl font-bold text-white">
              Ready to start earning?
            </h3>
                <p className="text-sm text-[#8A92A0]">Every artist launches with the same fortress under them.</p>
          </div>
          <div className="flex gap-3">
            <Link
              to="/dashboard"
              className="inline-flex items-center gap-2 h-11 px-5 rounded-full bg-[#EC008C] text-black font-bold hover:bg-[#FF3399]"
              data-testid="cta-dashboard"
            >
              <Ticket className="w-4 h-4" /> Open my dashboard
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default TeachingCenter;
